import { graphql, PageProps } from "gatsby";
import React, { useMemo, useState } from "react";
import { render as toPlainText } from "datocms-structured-text-to-plain-text";
import Breadcrumbs, { BreadcrumbItem } from "../components/Breadcrumbs";
import LanguageBanner from "../components/LanguageBanner";
import Layout, { PageContextProps } from "../components/Layout";
import PageHead from "../components/PageHead";
import EventPageSectionAgenda from "../components/sections/EventPageSectionAgenda";
import EventPageSectionEmployees from "../components/sections/EventPageSectionEmployees";
import EventPageSectionLead from "../components/sections/EventPageSectionLead";
import EventPageSectionPageTitle from "../components/sections/EventPageSectionPageTitle";
import EventPageSectionPrimaryPhoto from "../components/sections/EventPageSectionPrimaryPhoto";
import EventPageSectionPrimaryVideo from "../components/sections/EventPageSectionPrimaryVideo";
import EventPageSectionRegistration from "../components/sections/EventPageSectionRegistration";
import EventPageSectionRunningText from "../components/sections/EventPageSectionRunningText";
import EventPageSectionSpeakers from "../components/sections/EventPageSectionSpeakers";
import { isPastEvent } from "../helpers/dateTimeDatoString";
import { getFieldFromLocale } from "../helpers/getFieldFromLocale";
import useLocalizedTags from "../hooks/useLocalizedTags";

export type EventPageUiLabels =
  Queries.EventPageQuery["datoCmsLabel"]["eventPage"][0];

const EventPage = (
  props: PageProps<Queries.EventPageQuery, PageContextProps>
) => {
  const {
    data: {
      datoCmsEvent,
      datoCmsLabel: {
        eventPage: [uiLabels],
        uiLabelsContentVideo: [uiLabelsContentVideo],
      },
      datoCmsLanguageBanner: {
        bannerTextOneLanguage,
        bannerTextMultipleLanguages,
      },
    },
    pageContext: {
      locale,
      pathLocales,
      searchPath,
      breadcrumbItems,
      isNotInCurrentLocale,
      availableLocales,
      fallbackLocales,
    },
  } = props;

  const [contentLocale, setContentLocale] = useState(() =>
    [locale, ...fallbackLocales].find((loc) => availableLocales.includes(loc))
  );

  const {
    locales,
    title,
    from,
    to,
    location: [location],
    allGroupServicePagesReferencingThisEvent: groupServicePages,
    primaryVideo,
    primaryPhoto,
    lead,
    runningText,
    sectionAgenda,
    sectionSpeakers,
    sectionRegistration,
    sectionEmployees: [sectionEmployees],
    _allTitleLocales = [{ locale, value: title }],
    _allLeadLocales = [{ locale, value: lead }],
    _allPrimaryPhotoLocales = [{ locale, value: primaryPhoto }],
    _allPrimaryVideoLocales = [{ locale, value: primaryVideo }],
    _allRunningTextLocales = [{ locale, value: runningText }],
    _allSectionAgendaLocales = [{ locale, value: sectionAgenda }],
    _allSectionSpeakersLocales = [{ locale, value: sectionSpeakers }],
    _allSectionEmployeeAtTheEventLocales = [
      { locale, value: sectionEmployees },
    ],
    _allSectionRegistrationLocales = [
      {
        locale,
        value: sectionRegistration,
      },
    ],
  } = datoCmsEvent;

  const groupServices = groupServicePages.map((e) => e.groupService);

  const data = useMemo(
    () =>
      locales.reduce(
        (acc, locale) => ({
          ...acc,
          [locale]: {
            title: getFieldFromLocale(_allTitleLocales, locale),
            lead: getFieldFromLocale(_allLeadLocales, locale),
            primaryPhoto: getFieldFromLocale(_allPrimaryPhotoLocales, locale),
            primaryVideo: getFieldFromLocale(_allPrimaryVideoLocales, locale),
            runningText: getFieldFromLocale(_allRunningTextLocales, locale),
            sectionAgenda: getFieldFromLocale(_allSectionAgendaLocales, locale),
            sectionSpeakers: getFieldFromLocale(
              _allSectionSpeakersLocales,
              locale
            ),
            sectionRegistration: getFieldFromLocale(
              _allSectionRegistrationLocales,
              locale
            ),
            sectionEmployees: getFieldFromLocale(
              _allSectionEmployeeAtTheEventLocales,
              locale
            ),
          },
        }),
        {}
      ),
    []
  );

  const tags = useLocalizedTags(
    isNotInCurrentLocale,
    pathLocales,
    locales,
    locale
  );

  const link = data[
    contentLocale
  ].sectionRegistration[0].registrationLink.startsWith("http")
    ? data[contentLocale].sectionRegistration[0].registrationLink
    : `mailto:${data[contentLocale].sectionRegistration[0].registrationLink}`;

  const getSectionActiveSpeakers = () => {
    if (data[contentLocale].sectionSpeakers.length === 0) {
      return data[contentLocale].sectionSpeakers;
    }

    const activeSpeakers = data[contentLocale].sectionSpeakers[0].speakers.map(
      (speakerItem) => {
        switch (speakerItem.speaker[0].__typename) {
          case "DatoCmsEmployee":
            return !speakerItem.speaker[0].activeEmployee &&
              speakerItem.speaker[0].pagePath
              ? {
                  ...speakerItem,
                  speaker: [{ ...speakerItem.speaker[0], pagePath: null }],
                }
              : speakerItem;

          case "DatoCmsExternal":
          default:
            return speakerItem;
        }
      }
    );

    return [{ speakers: activeSpeakers }];
  };

  const sectionActiveSpeakers = getSectionActiveSpeakers();

  const getSectionActiveEmployees = () => {
    if (
      !data[contentLocale].sectionEmployees ||
      !data[contentLocale].sectionEmployees[0]
    ) {
      return sectionEmployees;
    }

    const activeEmployees = data[
      contentLocale
    ].sectionEmployees[0].items.filter((employee) => employee.activeEmployee);

    return {
      ...data[contentLocale].sectionEmployees[0],
      items: activeEmployees,
    };
  };

  const sectionActiveEmployees = getSectionActiveEmployees();

  const onEventLangChange = (locale: string) => {
    setContentLocale(locale);
  };

  return (
    <Layout locale={locale} pathLocales={pathLocales} searchPath={searchPath}>
      <Breadcrumbs breadcrumbItems={breadcrumbItems} />
      {isNotInCurrentLocale ? (
        <LanguageBanner
          text={
            tags.length > 1
              ? bannerTextMultipleLanguages
              : bannerTextOneLanguage
          }
          languages={tags}
          selectedLocale={contentLocale}
          onLanguageSelected={onEventLangChange}
        />
      ) : null}
      <div className="sections-container">
        <EventPageSectionPageTitle
          title={data[contentLocale].title}
          from={from}
          to={to}
          location={location}
          groupServices={groupServices}
          registrationLink={link}
          additionalInformation={
            data[contentLocale].sectionRegistration[0].additionalInformation
          }
          uiLabels={uiLabels}
          locale={locale}
          isBannerVisible={isNotInCurrentLocale}
        />

        {primaryVideo ? (
          <EventPageSectionPrimaryVideo
            primaryVideo={data[contentLocale].primaryVideo}
            uiLabelsContentVideo={uiLabelsContentVideo}
          />
        ) : (
          <EventPageSectionPrimaryPhoto
            primaryPhoto={data[contentLocale].primaryPhoto}
          />
        )}

        <EventPageSectionLead lead={data[contentLocale].lead} />

        <EventPageSectionRunningText
          runningText={data[contentLocale].runningText}
        />

        {sectionAgenda.length > 0 ? (
          <EventPageSectionAgenda
            sectionAgenda={data[contentLocale].sectionAgenda}
            uiLabels={uiLabels}
          />
        ) : null}

        {sectionActiveSpeakers.length > 0 ? (
          <EventPageSectionSpeakers sectionSpeakers={sectionActiveSpeakers} />
        ) : null}

        {!isPastEvent(from, to) ? (
          <EventPageSectionRegistration
            location={location}
            title={data[contentLocale].sectionRegistration[0].title}
            description={data[contentLocale].sectionRegistration[0].description}
            registrationLink={link}
            additionalInformation={
              data[contentLocale].sectionRegistration[0].additionalInformation
            }
            uiLabels={uiLabels}
          />
        ) : null}

        {sectionActiveEmployees ? (
          <EventPageSectionEmployees
            title={sectionActiveEmployees.title}
            items={sectionActiveEmployees.items}
          />
        ) : null}
      </div>
    </Layout>
  );
};

type MetaTag = {
  tagName: string;
  content?: string;
  attributes?: Record<string, string>;
};

interface StructuredDataProps {
  datoCmsEvent: Queries.EventPageQuery["datoCmsEvent"];
  faviconMetaTags: Queries.EventPageQuery["datoCmsSite"]["faviconMetaTags"];
  uiLabelsOnline: string;
  breadcrumbItems: BreadcrumbItem[] | undefined;
}

const StructuredData = ({
  datoCmsEvent,
  faviconMetaTags,
  uiLabelsOnline,
  breadcrumbItems,
}: StructuredDataProps) => {
  const siteURL = process.env.GATSBY_SITE_URL ?? "";
  const isPrimaryDomain = siteURL === "https://foryouandyourcustomers.com";

  if (!isPrimaryDomain) {
    return null;
  }

  const faviconTags = faviconMetaTags.tags as unknown as MetaTag[];
  const logoURL =
    faviconTags.find((tag) => tag.attributes?.rel === "apple-touch-icon")
      ?.attributes?.href ??
    faviconTags.find((tag) => tag.attributes?.rel?.includes("icon"))?.attributes
      ?.href;

  const {
    pagePath,
    seoMetaTags,
    title,
    lead,
    primaryPhoto,
    from,
    to,
    location: [location],
    sectionRegistration: [sectionRegistration],
    sectionSpeakers: [sectionSpeakers],
  } = datoCmsEvent;
  const pageURL = `${siteURL}${pagePath}`;

  const seoTags = seoMetaTags.tags as unknown as MetaTag[];
  const pageName = seoTags.find((tag) => tag.tagName === "title")?.content;

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const headline = `${toPlainText(title as any)}`;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const description = lead ? `${toPlainText(lead as any)}` : undefined;

  const breadcrumbListItems = breadcrumbItems?.length
    ? [
        ...breadcrumbItems.slice(0, -1),
        { ...breadcrumbItems[breadcrumbItems.length - 1], label: headline },
      ]
    : undefined;

  const isOnlineEvent =
    location?.__typename === "DatoCmsEventPageOnlineLocation";

  const registrationLink = sectionRegistration?.registrationLink;
  const virtualLocationUrl = registrationLink?.startsWith("http")
    ? registrationLink
    : pageURL;

  const eventLocation = isOnlineEvent
    ? {
        "@type": "VirtualLocation",
        name: uiLabelsOnline,
        url: virtualLocationUrl,
      }
    : location
    ? {
        "@type": "Place",
        name: location.name,
        address: {
          "@type": "PostalAddress",
          streetAddress: location.streetAndNumber,
          addressLocality: location.city,
          addressCountry: location.country?.label,
        },
      }
    : undefined;

  const performers = (sectionSpeakers?.speakers ?? [])
    .map((item) => item.speaker?.[0])
    .filter(Boolean)
    .map((speaker) =>
      speaker.__typename === "DatoCmsEmployee"
        ? {
            "@type": "Person",
            name: speaker.fullname,
            jobTitle: speaker.jobTitle,
            ...(speaker.activeEmployee && speaker.pagePath
              ? { url: `${siteURL}${speaker.pagePath}` }
              : {}),
          }
        : {
            "@type": "Person",
            name: speaker.fullname,
            jobTitle: speaker.jobTitle,
          }
    );

  const structuredData = {
    "@context": "https://schema.org",
    "@graph": [
      {
        "@type": "Organization",
        "@id": `${siteURL}/#organization`,
        name: "foryouandyourcustomers",
        url: `${siteURL}/`,
        logo: logoURL?.split("?")[0],
      },
      {
        "@type": "WebPage",
        "@id": `${pageURL}#webpage`,
        url: pageURL,
        name: pageName,
        isPartOf: {
          "@id": `${siteURL}/#website`,
        },
      },
      {
        "@type": "BusinessEvent",
        "@id": `${pageURL}#event`,
        name: headline,
        description,
        startDate: from,
        endDate: to,
        eventAttendanceMode: isOnlineEvent
          ? "https://schema.org/OnlineEventAttendanceMode"
          : "https://schema.org/OfflineEventAttendanceMode",
        eventStatus: "https://schema.org/EventScheduled",
        ...(eventLocation ? { location: eventLocation } : {}),
        image: primaryPhoto?.url,
        ...(performers.length ? { performer: performers } : {}),
        sponsor: {
          "@id": `${siteURL}/#organization`,
        },
      },
      ...(breadcrumbListItems
        ? [
            {
              "@type": "BreadcrumbList",
              "@id": `${pageURL}#breadcrumb`,
              itemListElement: breadcrumbListItems.map((item, index) => ({
                "@type": "ListItem",
                position: index + 1,
                name: item.label,
                item: `${siteURL}${item.path ?? pagePath}`,
              })),
            },
          ]
        : []),
    ],
  };

  return (
    <script type="application/ld+json">{JSON.stringify(structuredData)}</script>
  );
};

export const Head = (
  props: PageProps<Queries.EventPageQuery, PageContextProps>
) => {
  const {
    data: {
      datoCmsSite: { faviconMetaTags },
      datoCmsEvent,
      datoCmsLabel: {
        eventPage: [uiLabels],
      },
    },
    pageContext: { locale, pathLocales, availableLocales, breadcrumbItems },
  } = props;

  const { seoMetaTags, locales } = datoCmsEvent;
  const isContentInLocale = locales.indexOf(locale) !== -1;

  return (
    <>
      <PageHead
        locale={locale}
        seoMetaTags={seoMetaTags}
        faviconMetaTags={faviconMetaTags}
        pathLocales={pathLocales}
        availableLocales={availableLocales}
      />
      {isContentInLocale ? (
        <StructuredData
          datoCmsEvent={datoCmsEvent}
          faviconMetaTags={faviconMetaTags}
          uiLabelsOnline={uiLabels.online}
          breadcrumbItems={breadcrumbItems}
        />
      ) : null}
    </>
  );
};

export const query = graphql`
  query EventPage(
    $id: String!
    $locale: String!
    $fallbackLocales: [String!]
    $isNotInCurrentLocale: Boolean!
  ) {
    datoCmsSite(locale: $locale, fallbackLocales: $fallbackLocales) {
      faviconMetaTags {
        tags
      }
    }
    datoCmsEvent(
      id: { eq: $id }
      locale: $locale
      fallbackLocales: $fallbackLocales
    ) {
      id
      locales
      seoMetaTags {
        tags
      }
      pagePath(locale: $locale)
      title {
        value
      }
      _allTitleLocales @include(if: $isNotInCurrentLocale) {
        locale
        value {
          value
        }
      }
      from
      to
      location {
        __typename
        ... on DatoCmsEventPagePhysicalLocation {
          name
          streetAndNumber
          zipcode
          city
          country {
            label
          }
        }
        ... on DatoCmsEventPageOnlineLocation {
          online
        }
      }
      allGroupServicePagesReferencingThisEvent {
        groupService {
          name
          pagePath(locale: $locale)
          internalService
        }
      }
      primaryPhoto {
        alt
        url(imgixParams: { w: "1200", auto: "format", q: 60 })
        gatsbyImageData(aspectRatio: 1.77)
      }
      _allPrimaryPhotoLocales @include(if: $isNotInCurrentLocale) {
        locale
        value {
          alt
          gatsbyImageData(aspectRatio: 1.77)
        }
      }
      primaryVideo {
        url
        title
      }
      _allPrimaryVideoLocales @include(if: $isNotInCurrentLocale) {
        locale
        value {
          url
          title
        }
      }
      lead {
        value
        links {
          ...StructuredTextGenericLinks
        }
      }
      _allLeadLocales @include(if: $isNotInCurrentLocale) {
        locale
        value {
          value
          links {
            ...StructuredTextGenericLinks
          }
        }
      }
      runningText {
        value
        blocks {
          ...StructuredTextGenericBlocks
        }
        links {
          ...StructuredTextGenericLinks
        }
      }
      _allRunningTextLocales @include(if: $isNotInCurrentLocale) {
        locale
        value {
          value
          blocks {
            ...StructuredTextGenericBlocks
          }
          links {
            ...StructuredTextGenericLinks
          }
        }
      }
      sectionAgenda {
        agenda: items {
          from
          description {
            value
            links {
              ...StructuredTextGenericLinks
            }
          }
        }
      }
      _allSectionAgendaLocales @include(if: $isNotInCurrentLocale) {
        locale
        value {
          agenda: items {
            from
            description {
              value
              links {
                ...StructuredTextGenericLinks
              }
            }
          }
        }
      }
      sectionSpeakers {
        speakers: items {
          speaker {
            ... on DatoCmsEmployee {
              __typename
              activeEmployee
              fullname
              jobTitle
              pagePath(locale: $locale)
              primaryPhoto {
                alt
                gatsbyImageData(aspectRatio: 1.77)
              }
            }
            ... on DatoCmsExternal {
              __typename
              fullname
              jobTitle
              company
              primaryPhoto {
                alt
                gatsbyImageData(aspectRatio: 1.77)
              }
            }
          }
          introduction {
            value
            links {
              ...StructuredTextGenericLinks
            }
          }
        }
      }
      _allSectionSpeakersLocales @include(if: $isNotInCurrentLocale) {
        locale
        value {
          speakers: items {
            speaker {
              ... on DatoCmsEmployee {
                __typename
                activeEmployee
                fullname
                jobTitle
                pagePath(locale: $locale)
                primaryPhoto {
                  alt
                  gatsbyImageData(aspectRatio: 1.77)
                }
              }
              ... on DatoCmsExternal {
                __typename
                fullname
                jobTitle
                company
                primaryPhoto {
                  alt
                  gatsbyImageData(aspectRatio: 1.77)
                }
              }
            }
            introduction {
              value
              links {
                ...StructuredTextGenericLinks
              }
            }
          }
        }
      }
      sectionRegistration {
        title
        registrationLink
        additionalInformation {
          ... on DatoCmsEventPageAdditionalInformationCalendar {
            calendarFile {
              url
              filename
            }
          }
          ... on DatoCmsEventPageAdditionalInformationDirection {
            googleMapsLink
          }
        }
        description {
          value
          links {
            ...StructuredTextGenericLinks
          }
        }
      }
      _allSectionRegistrationLocales @include(if: $isNotInCurrentLocale) {
        locale
        value {
          title
          registrationLink
          additionalInformation {
            ... on DatoCmsEventPageAdditionalInformationCalendar {
              calendarFile {
                url
                filename
              }
            }
            ... on DatoCmsEventPageAdditionalInformationDirection {
              googleMapsLink
            }
          }
          description {
            value
            links {
              ...StructuredTextGenericLinks
            }
          }
        }
      }
      sectionEmployees: sectionEmployeeAtTheEvent {
        title {
          category
          title
        }
        items {
          activeEmployee
          fullname
          jobTitle
          shortDescription
          pagePath(locale: $locale)
          primaryPhoto {
            alt
            gatsbyImageData(aspectRatio: 1.77)
          }
        }
      }
      _allSectionEmployeeAtTheEventLocales @include(if: $isNotInCurrentLocale) {
        locale
        value {
          title {
            category
            title
          }
          items {
            activeEmployee
            fullname
            jobTitle
            shortDescription
            pagePath(locale: $locale)
            primaryPhoto {
              alt
              gatsbyImageData(aspectRatio: 1.77)
            }
          }
        }
      }
    }
    datoCmsLabel(locale: $locale, fallbackLocales: $fallbackLocales) {
      eventPage {
        language
        online
        agenda
        buttonRegister
        buttonGetDirections
        buttonAddToCalendar
        eventEndedTag
      }
      uiLabelsContentVideo: contentVideo {
        mediaNotVisibleText
        reviewCookiesButtonLabel
      }
    }
    datoCmsLanguageBanner(locale: $locale, fallbackLocales: $fallbackLocales) {
      bannerTextMultipleLanguages
      bannerTextOneLanguage
    }
  }
`;

export default EventPage;
